import React from "react";

import { withTranslation } from "react-i18next";
import { Message } from "src/modules/communication-protobuf";
import {
  removeDeviceFromFamily,
  unpairDevice,
  setDeviceName
} from "src/modules/device/ajax/cloud";
import Dialog from "src/components/Dialog";
import IncorrectPasswordDialog from "src/modules/auth/components/IncorrectPasswordDialog";
import GenericFailureDialog from "src/components/GenericFailureDialog";
import ScrollBox from "src/atoms/ScrollBox";
import WithNavigationBarLayout from "src/components/WithNavigationBarLayout";

import DeviceList from "./components/DeviceList";
import RemoveFamilyDeviceWithPasswordDialog from "./components/RemoveFamilyDeviceWithPasswordDialog";
import RemoveFamilyDeviceDialog from "./components/RemoveFamilyDeviceDialog";
import RemoveOtherDeviceDialog from "./components/RemoveOtherDeviceDialog";
import RenameDeviceDialog from "./components/RenameDeviceDialog";

const log = window.require("electron-log");

const DialogType = {
  INCORRECT_PASSWORD: "INCORRECT_PASSWORD",
  REMOVE_DEVICE: "REMOVE_DEVICE",
  REMOVE_DEVICE_WITH_PASSWORD: "REMOVE_DEVICE_WITH_PASSWORD",
  REMOVE_OTHER_DEVICE: "REMOVE_OTHER_DEVICE",
  RENAME_DEVICE: "RENAME_DEVICE",
  FAILURE: "FAILURE",
  PROCESSING: "PROCESSING"
};

class PairedDevices extends React.Component {
  state = {
    openedDialog: null,
    deviceToRemove: {},
    deviceToRename: {},
    password: ""
  };

  handlePasswordChange = (event) =>
    this.setState({ password: event.target.value });

  handleFamilyDeviceRemoval = (deviceToRemove) => {
    const { hasFamilyAccount } = this.props;
    if (hasFamilyAccount) {
      this.setState({
        openedDialog: DialogType.REMOVE_DEVICE_WITH_PASSWORD,
        deviceToRemove
      });
    } else {
      this.setState({
        openedDialog: DialogType.REMOVE_DEVICE,
        deviceToRemove
      });
    }
  };

  handleDeviceRename = (deviceToRename) => {
    this.setState({
      openedDialog: DialogType.RENAME_DEVICE,
      deviceToRename
    });
  };

  renameDevice = async (deviceName) => {
    const { deviceToRename } = this.state;
    const { app } = this.props;
    this.setState({ openedDialog: DialogType.PROCESSING });
    try {
      await setDeviceName({ xmppLogin: deviceToRename.jid, deviceName });
      app.synchronizer.announceItemChange(
        app.synchronizer.SynchronizableItems.DEVICES
      );
      app.synchronizer.synchronizeDevices();
      this.setState({ openedDialog: null });
    } catch (err) {
      this.setState({ openedDialog: DialogType.FAILURE });
    } finally {
      this.setState({ deviceToRename: {} });
    }
  };

  removeFamilyDevice = async () => {
    const { deviceToRemove, password } = this.state;
    const { removePairedDevice, messenger } = this.props;
    try {
      this.setState({ openedDialog: DialogType.PROCESSING });
      await removeDeviceFromFamily({ xmppLogin: deviceToRemove.jid, password });
      removePairedDevice(deviceToRemove.jid);
      messenger.sendAsEnvelope({
        to: deviceToRemove.jid,
        payload: {
          message: Message.DeviceUnpairedMessage.create()
        }
      });
      this.setState({ openedDialog: null });
    } catch (error) {
      log.error(error);
      const invalidPasswordError =
        error.message === "Not Authorized" ||
        error.message === "Invalid username/password.";
      if (password && invalidPasswordError) {
        this.setState({ openedDialog: DialogType.INCORRECT_PASSWORD });
      } else {
        this.setState({ openedDialog: DialogType.FAILURE });
      }
    } finally {
      this.setState({ deviceToRemove: {}, password: "" });
    }
  };

  handleOtherDeviceRemoval = (deviceToRemove) => {
    this.setState({
      openedDialog: DialogType.REMOVE_OTHER_DEVICE,
      deviceToRemove
    });
  };

  removeOtherDevice = async () => {
    const { deviceToRemove } = this.state;
    const { removePairedDevice } = this.props;
    try {
      this.setState({ openedDialog: DialogType.PROCESSING });
      await unpairDevice({ xmppLogin: deviceToRemove.jid });
      removePairedDevice(deviceToRemove.jid);
      this.setState({ openedDialog: null });
    } catch (error) {
      log.error(error);
      this.setState({ openedDialog: DialogType.FAILURE });
    } finally {
      this.setState({ deviceToRemove: {} });
    }
  };

  dismissDialog = () => this.setState({ openedDialog: null });

  render() {
    const { deviceToRemove, deviceToRename, password, openedDialog } =
      this.state;
    const { t, onClose, onBack, openWizard } = this.props;

    return (
      <>
        <WithNavigationBarLayout
          navigationBarProps={{
            headingKey: "pairedDevices.heading",
            onClose,
            onBack,
            actionButton: {
              labelKey: "buttons.pairNewDevice",
              onClick: openWizard
            }
          }}
          childrenBoxProps={{ py: "16px" }}
        >
          <ScrollBox>
            <DeviceList
              handleFamilyDeviceRemoval={this.handleFamilyDeviceRemoval}
              handleOtherDeviceRemoval={this.handleOtherDeviceRemoval}
              handleDeviceRename={this.handleDeviceRename}
            />
          </ScrollBox>
        </WithNavigationBarLayout>
        <Dialog
          isOpen={openedDialog === DialogType.PROCESSING}
          isProcessing={true}
          processingText={
            deviceToRemove &&
            Object.keys(deviceToRemove).length !== 0 &&
            t("pairedDevices.removing")
          }
        />
        <RenameDeviceDialog
          isOpen={openedDialog === DialogType.RENAME_DEVICE}
          onDismiss={this.dismissDialog}
          onRename={this.renameDevice}
          deviceName={deviceToRename.name}
          originalDeviceName={deviceToRename?.originalName}
        />
        <RemoveFamilyDeviceWithPasswordDialog
          isOpen={openedDialog === DialogType.REMOVE_DEVICE_WITH_PASSWORD}
          onDismiss={this.dismissDialog}
          onRemove={this.removeFamilyDevice}
          onChange={this.handlePasswordChange}
          password={password}
          deviceName={deviceToRemove.name}
        />
        <RemoveFamilyDeviceDialog
          isOpen={openedDialog === DialogType.REMOVE_DEVICE}
          onDismiss={this.dismissDialog}
          onRemove={this.removeFamilyDevice}
          deviceName={deviceToRemove.name}
        />
        <RemoveOtherDeviceDialog
          isOpen={openedDialog === DialogType.REMOVE_OTHER_DEVICE}
          onDismiss={this.dismissDialog}
          onRemove={this.removeOtherDevice}
          deviceName={deviceToRemove.name}
        />
        <IncorrectPasswordDialog
          isOpen={openedDialog === DialogType.INCORRECT_PASSWORD}
          onDismiss={this.dismissDialog}
        />
        <GenericFailureDialog
          isOpen={openedDialog === DialogType.FAILURE}
          onDismiss={this.dismissDialog}
        />
      </>
    );
  }
}

export default withTranslation()(PairedDevices);
